/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useContext, useState, useCallback, useRef,
} from 'react';
import {
  Typography,
  Dialog,
  Grid,
  Button,
  makeStyles,
  Box,
  DialogTitle,
  Link,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import clsx from 'clsx';
import { API } from 'aws-amplify';
import CustomTextField from './CustomTextField';
import { getCompanyThemeData } from '../Theme';
import {
  $color8,
  $semiBold,
  $19pt,
  $14pt,
  $color6,
  $color7,
  $color11,
  $color15,
  $12pt,
  $10pt,
  $color39,
} from '../styles/Variables';
import EmailImage from '../images/email.svg';
import Countdown from './Countdown';
import config from '../configs';
import { AuthenticationContext } from '../contexts/Authentication';
import OTP from './OTP';
import { APIS } from '../configs/amplify';
import { encodeRedirect } from '../utils/url';

const useStyles = makeStyles((theme) => {
  const companyTheme = getCompanyThemeData();
  return {
    dialogTitle__root: {
      padding: 0,
      marginBottom: theme.spacing(2),
    },
    dialog__paper: {
      padding: '25px 30px',
      outline: 0,
      [theme.breakpoints.only('xs')]: {
        padding: '25px 15px',
        margin: '15px',
      },
    },
    dialogContentTitleTextStyle: {
      fontWeight: $semiBold,
      fontSize: $19pt,
      textAlign: 'center',
      color: $color8,
    },
    dialogContentSubtitleTextStyle: {
      fontSize: $14pt,
      textAlign: 'center',
      color: $color6,
      padding: theme.spacing(1, 0),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(1, 2, 2),
      },
    },
    submit: {
      margin: theme.spacing(1.5, 0),
      backgroundColor: companyTheme.buttonColor,
      textTransform: 'unset',
    },
    buttonTextColor: {
      color: companyTheme.buttonTextColor,
      fontWeight: $semiBold,
    },
    imgStyle: {
      margin: 'auto',
    },
    clearIcon: {
      float: 'right',
      cursor: 'pointer',
    },
    changeEmailButton: {
      fontWeight: $semiBold,
      fontSize: $14pt,
      color: $color7,
      textTransform: 'unset',
    },
    linkButton: {
      fontSize: $12pt,
      textDecoration: 'unset !important',
    },
    rightAlign: {
      float: 'right',
    },
    displayFlex: {
      display: 'flex',
    },
    disabledLink: {
      color: `${$color11} !important`,
      cursor: 'default',
    },
    resendLink: {
      color: $color15,
    },
    changeEmailLink: {
      color: $color7,
    },
    errorText: {
      textAlign: 'center',
      fontSize: $10pt,
      color: $color39,
    },
    resendText: {
      textAlign: 'center',
      fontSize: $10pt,
      color: $color6,
    },
    hide: {
      display: 'none',
    },
    skipLink: {
      margin: '0 auto',
      color: $color7,
      textAlign: 'center',
      fontSize: $12pt,
    },
  };
});

interface IEmailModalProps {
  userEmail: string;
}

enum ERRORS {
  invalid = 'Hmm... your OTP seems to be invalid. Please try again.',
  maximum = 'Seems like you\'ve reached the maximum number of attempts!\nPlease request for a new one.',
  expire = 'Oh uh, your OTP has expired. Please request for a new one.'
}

export default function EmailModal({ userEmail }: IEmailModalProps) {
  const classes = useStyles();
  const [step, setStep] = useState<0 | 1>(0);
  const [email, setEmail] = useState(userEmail);
  const [isResend, setIsResend] = useState(false);
  const [isTimesUp, setIsTimesUp] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [isErrorMessage, setIsErrorMessage] = useState('');
  const [isEmailDuplicated, setIsEmailDuplicated] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [otp, setOtp] = useState('');
  const emailRef = useRef('');
  const companyTheme = getCompanyThemeData();
  const authenticationContext = useContext(AuthenticationContext);
  const {
    state: { userData: { companyID = '', userName = '' } = {} },
    actions: { authenticate },
  } = authenticationContext;

  const submitable: boolean = !emailLoading
    && !!email
    && /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
      email,
    );

  const otpSubmitable: boolean = !otpLoading && otp?.length === 6;

  const onHandleEmail = useCallback((e) => setEmail(e.target.value), []);
  const onHandleOTP = useCallback((newValue: string) => setOtp(newValue), []);

  const navigateSRB = useCallback(() => {
    window.location.href = encodeRedirect() || config.companyMarketplaceURL;
  }, []);

  const onEmailSubmit = useCallback(async () => {
    if (step === 1 || emailRef.current !== email) {
      try {
        setEmailLoading(true);
        await API.put(APIS.registration, `/profiles/${userName}/email`, {
          headers: {
            'x-company-name': companyTheme.pageTitle,
            'x-company-logo': config.companyLogoUrl,
          },
          body: {
            id: userName,
            company_id: companyID,
            email_update: email,
          },
        });
        setIsTimesUp(false);
        setIsErrorMessage('');
        setIsResend(Boolean(step));
        setIsEmailDuplicated(false);
        emailRef.current = email || '';
      } catch (ex) {
        if (ex?.response?.data?.error_message === 'Email address has been taken') {
          setIsEmailDuplicated(true);
        } else {
          setSnackbarMessage('Something went wrong. Please try later');
        }

        return;
      } finally {
        setEmailLoading(false);
      }
    }
    setStep(1);
  }, [companyTheme, email, step, companyID, userName]);

  const onOTPSubmit = useCallback(async () => {
    try {
      setOtpLoading(true);
      await API.put(APIS.registration, `/profiles/${userName}/email_confirm`, {
        body: {
          id: userName,
          company_id: companyID,
          email_update: email,
          company_user_pool_id: config.cognitoAWSUserPoolID,
          email_otp: otp,
        },
      });
      await authenticate({ bypassCache: true });
    } catch (e) {
      const { error_message: message } = e?.response?.data;

      switch (message) {
        case 'Invalid OTP, please try again':
          setIsErrorMessage(ERRORS.invalid);
          break;

        case 'Maximum OTP validation attempts exceeded. Please request OTP again':
          setIsErrorMessage(ERRORS.maximum);
          break;

        case 'OTP has expired, please request OTP again':
          setIsErrorMessage(ERRORS.expire);
          break;

        default:
          setSnackbarMessage('Something went wrong. Please try later');
      }
    } finally {
      setOtpLoading(false);
    }
  }, [companyID, email, otp, userName, authenticate]);


  const renderForm = useCallback(
    () => (
      <>
        <Box mb={2} display="flex">
          <img src={EmailImage} alt="email" className={classes.imgStyle} />
        </Box>

        <Typography className={classes.dialogContentTitleTextStyle}>
          Verify your email
        </Typography>
        <Typography className={classes.dialogContentSubtitleTextStyle}>
          Provide your email to receive a one-time password. This will only need
          to be done once.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTextField
              value={email}
              required
              fullWidth
              label="Your email"
              name="email"
              autoComplete="email"
              autoFocus
              type="email"
              onChange={onHandleEmail}
              placeholder="example@workmail.com"
              disabled={emailLoading}
            />
          </Grid>

          {isEmailDuplicated && (
            <Box marginX="auto" marginTop={0.5}>
              <Typography className={classes.errorText}>
                Seems like that email is already in use, can you try a different email?
              </Typography>
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={!submitable}
            className={classes.submit}
            onClick={onEmailSubmit}
          >
            {emailLoading ? <CircularProgress size={25} />
              : (
                <Typography className={classes.buttonTextColor}>
                  Send OTP
                </Typography>
              )}
          </Button>

          <Link
            underline="none"
            href="#"
            className={classes.skipLink}
            onClick={navigateSRB}
          >
            Skip for now
          </Link>
        </Grid>
      </>
    ),
    [classes, emailLoading, onHandleEmail, submitable,
      onEmailSubmit, navigateSRB, email, isEmailDuplicated],
  );

  const renderConfirmation = useCallback(
    () => (
      <Box marginX={[0, 3.5]}>
        <Typography className={classes.dialogContentTitleTextStyle}>
          One-time password has been sent
        </Typography>
        <Typography className={classes.dialogContentSubtitleTextStyle}>
          Please enter the 6-digit code we’ve sent to
          {' '}
          <b>{email}</b>
        </Typography>

        <Box marginX={[0, 2]} marginBottom={1.5}>
          <OTP
            onDataChange={onHandleOTP}
            disabled={otpLoading}
          />
        </Box>

        <Typography
          className={clsx(classes.resendText,
            { [classes.hide]: !isResend || Boolean(isErrorMessage) })}
        >
          The code has been resent to your email
        </Typography>


        {isErrorMessage.split('\n').map((text, index) => (
          <Typography
            key={index}
            className={clsx(classes.errorText, { [classes.hide]: !isErrorMessage })}
          >
            {text}
          </Typography>
        ))}


        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={!otpSubmitable || otpLoading}
          className={classes.submit}
          onClick={onOTPSubmit}
        >
          {otpLoading && otpSubmitable
            ? <CircularProgress size={25} />
            : <Typography className={classes.buttonTextColor}>Verify</Typography>}
        </Button>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            {isTimesUp ? (
              emailLoading ? (
                <CircularProgress size={15} />
              ) : (
                <Link
                  component="button"
                  disabled={emailLoading}
                  onClick={onEmailSubmit}
                  className={clsx(classes.linkButton, classes.resendLink)}
                >
                  Resend OTP
                </Link>
              )
            ) : (
              <div className={clsx(classes.displayFlex, classes.disabledLink)}>
                <Box mr={0.5}>{'Resend OTP in '}</Box>
                <Countdown start={60} onComplete={() => setIsTimesUp(true)} />
              </div>
            )}
          </Grid>
          <Grid item xs={6}>
            <Link
              className={clsx(classes.rightAlign, classes.linkButton, classes.changeEmailLink)}
              href="#"
              onClick={() => setStep(0)}
            >
              Entered the wrong email?
            </Link>
          </Grid>
        </Grid>

      </Box>
    ),
    [email, classes, otpSubmitable, isTimesUp, emailLoading, otpLoading,
      onHandleOTP, isErrorMessage, isResend, onEmailSubmit, onOTPSubmit],
  );

  return (
    <>
      <Dialog
        aria-labelledby="email-dialog"
        open
        classes={{ paper: classes.dialog__paper }}
      >
        <DialogTitle
          id="email-dialog-title"
          classes={{ root: classes.dialogTitle__root }}
        >
          <ClearIcon
            className={classes.clearIcon}
            onClick={navigateSRB}
          />
        </DialogTitle>

        {!step ? renderForm() : renderConfirmation()}
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        onClose={() => setSnackbarMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={Boolean(snackbarMessage)}
        message={snackbarMessage}
      />
    </>
  );
}
